import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarHeart, Heart, MessageSquare, PhoneCall, Star, Users,
} from 'lucide-react';
import { coachKpiAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import CoachFollowupBar from '../profiles/CoachFollowupBar';
import ClientCrmModal from '../profiles/ClientCrmModal';

// 红娘工作台 — the coach's dashboard overview, rebuilt around 售后服务: what has
// been done for the clients they own (联系 / 跟进 / 约见 / 匹配) and what the
// clients said back (约会反馈). Every number comes from the records the CRM
// modal writes, so the page and the client detail can never disagree, and none
// of it depends on KPI / KPIv2.

// Luxe tokens (same values as StaffUI `S` / theme/luxe `L`).
const C = {
  line: 'rgba(17,17,17,.12)', lineSoft: 'rgba(17,17,17,.08)', ink: '#111111', ink2: '#3A3835', muted: '#6E685F', alt: '#EFEAE2',
  burgundy: '#111111', gold: '#8A6D3B', goldDecor: '#B8975A', card: '#FFFDF9', paper: '#F6F3EE',
  serif: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif",
  sans: "'Jost', 'Noto Sans SC', 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif",
};

const card = {
  background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 2,
  padding: 20, boxShadow: 'none',
};
const cardTitle = { fontFamily: C.serif, fontSize: 22, fontWeight: 500, color: C.ink, lineHeight: 1.2 };

export default function CoachOverview() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const t = (zh, en) => (isZh ? zh : en);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crmTarget, setCrmTarget] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await coachKpiAPI.getOverviewStats({ silent_days: 7 });
      setData(r.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, tick]);
  // Super coaches / managers get team-wide figures (backend scope 'team').
  const isTeam = data?.scope === 'team';

  const s = data || {};
  const clients = s.clients || {};
  const contacts = s.contacts || {};
  const followups = s.followups || {};
  const meetings = s.meetings || {};
  const matches = s.matches || {};
  const feedback = s.feedback || {};

  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const coverage = pct(contacts.clients_this_week || 0, clients.total || 0);
  const acceptRate = pct(matches.accepted || 0, matches.total || 0);

  // The four headline numbers a coach is measured on day to day.
  const heroCards = [
    {
      key: 'clients', Icon: Users, tone: C.burgundy,
      label: isTeam ? t('团队客户', 'Team clients') : t('我的客户', 'My clients'), value: clients.total || 0,
      sub: t(`本月新增 ${clients.new_this_month || 0} · 30天内到期 ${clients.expiring_30d || 0}`,
        `${clients.new_this_month || 0} new · ${clients.expiring_30d || 0} expiring`),
    },
    {
      key: 'contacts', Icon: PhoneCall, tone: '#3F7D5A',
      label: t('本周联系', 'Contacts this week'), value: contacts.this_week || 0,
      sub: t(`覆盖 ${contacts.clients_this_week || 0} 位客户 · ${coverage}%`,
        `${contacts.clients_this_week || 0} clients · ${coverage}%`),
    },
    {
      key: 'followups', Icon: Heart, tone: '#9E2B25',
      label: t('待跟进', 'Needs follow-up'), value: (followups.overdue || 0) + (followups.due_today || 0),
      sub: t(`逾期 ${followups.overdue || 0} · 今天 ${followups.due_today || 0} · 7天未联系 ${followups.silent || 0}`,
        `${followups.overdue || 0} overdue · ${followups.due_today || 0} today · ${followups.silent || 0} quiet`),
    },
    {
      key: 'meetings', Icon: CalendarHeart, tone: '#8A6D3B',
      label: t('即将约见', 'Upcoming dates'), value: meetings.upcoming || 0,
      sub: t(`本月 ${meetings.this_month || 0} 场 · 待写反馈 ${meetings.awaiting_feedback || 0}`,
        `${meetings.this_month || 0} this month · ${meetings.awaiting_feedback || 0} to review`),
    },
  ];

  // Grouped totals — the 售后服务 ledger, one column per kind of work.
  const groups = [
    {
      key: 'contact', title: t('联系 / 打卡', 'Contact log'), tone: '#3F7D5A',
      rows: [
        [t('本周', 'This week'), contacts.this_week || 0],
        [t('本月', 'This month'), contacts.this_month || 0],
        [t('累计', 'All time'), contacts.total || 0],
        [t('本周覆盖率', 'Weekly coverage'), `${coverage}%`],
      ],
    },
    {
      key: 'meet', title: t('约见安排', 'Dates arranged'), tone: '#8A6D3B',
      rows: [
        [t('即将进行', 'Upcoming'), meetings.upcoming || 0],
        [t('本月安排', 'This month'), meetings.this_month || 0],
        [t('本月完成', 'Completed'), meetings.completed_this_month || 0],
        [t('待写反馈', 'Awaiting feedback'), meetings.awaiting_feedback || 0],
      ],
    },
    {
      key: 'match', title: t('匹配签线', 'Matches'), tone: C.gold,
      rows: [
        [t('累计签线', 'All time'), matches.total || 0],
        [t('本月新增', 'This month'), matches.this_month || 0],
        [t('已确认', 'Accepted'), matches.accepted || 0],
        [t('确认率', 'Accept rate'), `${acceptRate}%`],
      ],
    },
    {
      key: 'quiet', title: t('跟进状态', 'Follow-up state'), tone: '#9E2B25',
      rows: [
        [t('已逾期', 'Overdue'), followups.overdue || 0],
        [t('今天要联系', 'Due today'), followups.due_today || 0],
        [t('本周要联系', 'Due this week'), followups.due_this_week || 0],
        [t('7天未联系', 'No contact 7d+'), followups.silent || 0],
      ],
    },
  ];

  const weekly = contacts.weekly || [];
  const weeklyMax = Math.max(1, ...weekly.map(w => w.count || 0));

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    return isZh ? `${d.getMonth() + 1}月${d.getDate()}日` : `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <div style={{ fontFamily: C.sans }}>
      {/* Welcome */}
      <div style={{
        background: 'transparent',
        color: C.ink, borderRadius: 0, padding: '0 0 20px', marginBottom: 24, borderBottom: `1px solid ${C.ink}`,
      }}>
        <h2 style={{ fontFamily: C.serif, fontSize: 'clamp(30px, 4vw, 42px)', fontWeight: 500, lineHeight: 1.1, color: C.ink, margin: 0 }}>
          {isTeam
            ? t('团队售后工作台', 'Team after-sales desk')
            : t(`${user?.first_name || user?.name || ''} 的红娘工作台`, `${user?.first_name || user?.name || ''}'s coaching desk`)}
        </h2>
        <p style={{ color: C.muted, margin: '8px 0 0', fontSize: 14, lineHeight: 1.65 }}>
          {isTeam
            ? t('整个红娘团队的售后服务总览：所有红娘的客户联系、跟进、约见、匹配与客户反馈的合计。',
              'After-sales overview for the whole coaching team: every coach’s contacts, follow-ups, dates, matches and client feedback.')
            : t('售后服务总览：客户联系、跟进、约见、匹配与客户反馈的合计。',
              'After-sales overview: contacts, follow-ups, dates, matches and what clients said back.')}
        </p>
      </div>

      {/* 今日跟进 — the same bar the client list carries, so today's work is the
          first thing on the dashboard too. */}
      <CoachFollowupBar
        isZh={isZh}
        silentDays={7}
        refreshKey={tick}
        onOpenClient={(c, action) => setCrmTarget({
          id: c.profile_id, first_name: c.first_name, last_name: c.last_name, profile_photo: c.profile_photo,
          initialAction: action || null,
        })}
      />

      {/* Headline stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
        {heroCards.map(c => (
          <div key={c.key} style={{ ...card, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 0, background: C.alt, color: c.tone, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
              <c.Icon size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: C.serif, fontSize: 38, fontWeight: 500, color: C.ink, lineHeight: 1 }}>
                {loading ? '—' : c.value}
              </div>
              <div style={{ fontSize: 11.5, fontWeight: 400, letterSpacing: '.2em', color: C.muted, marginTop: 8 }}>{c.label}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{c.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Grouped ledger */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, marginBottom: 14 }}>
        {groups.map(g => (
          <div key={g.key} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
              <span style={{ width: 6, height: 6, transform: 'rotate(45deg)', background: g.tone }} />
              <span style={cardTitle}>{g.title}</span>
            </div>
            {g.rows.map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0', borderTop: `1px solid ${C.lineSoft}` }}>
                <span style={{ fontSize: 12.5, color: C.muted }}>{label}</span>
                <b style={{ fontFamily: C.serif, fontSize: 20, fontWeight: 500, color: C.ink, lineHeight: 1.1 }}>{loading ? '—' : value}</b>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 12, alignItems: 'start' }}>
        {/* 联系趋势 */}
        <div style={card}>
          <div style={{ ...cardTitle, marginBottom: 14 }}>
            {t('最近 6 周联系量', 'Contacts · last 6 weeks')}
          </div>
          {weekly.length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.muted, padding: '12px 0' }}>
              {t('这段时间还没有打卡记录。', 'No check-ins logged in this period.')}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
              {weekly.map(w => (
                <div key={w.week_start} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: C.serif, fontSize: 15, fontWeight: 500, color: C.ink }}>{w.count}</span>
                  <div style={{
                    width: '100%', maxWidth: 34, borderRadius: 0,
                    height: `${Math.max(4, Math.round((w.count / weeklyMax) * 84))}px`,
                    background: C.ink,
                  }} />
                  <span style={{ fontSize: 10.5, color: C.muted }}>{fmtDate(w.week_start)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 客户反馈 */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <MessageSquare size={16} color={C.burgundy} />
            <span style={cardTitle}>{t('客户反馈', 'Client feedback')}</span>
            <span style={{ fontSize: 11.5, color: C.muted }}>
              {t(`累计 ${feedback.total || 0} 条 · 本月 ${feedback.this_month || 0} 条`,
                `${feedback.total || 0} total · ${feedback.this_month || 0} this month`)}
            </span>
            <div style={{ flex: 1 }} />
            {feedback.avg_rating ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: C.serif, fontSize: 18, fontWeight: 500, color: C.gold }}>
                <Star size={13} fill={C.gold} color={C.gold} /> {feedback.avg_rating}
              </span>
            ) : null}
          </div>

          {(feedback.recent || []).length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.muted, padding: '12px 0' }}>
              {t('还没有客户写回约会反馈。', 'No client feedback yet.')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {(feedback.recent || []).map(r => r.entries.map((e, i) => (
                <div key={`${r.id}-${i}`} style={{ padding: '10px 0', borderRadius: 0, background: 'transparent', borderTop: `1px solid ${C.lineSoft}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontFamily: C.serif, fontSize: 16, fontWeight: 500, color: C.ink }}>{e.name || t('客户', 'Client')}</span>
                    {e.rating ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 500, color: C.gold }}>
                        <Star size={11} fill={C.gold} color={C.gold} />{e.rating}
                      </span>
                    ) : null}
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, color: C.muted }}>{fmtDate(r.at)}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.6, overflowWrap: 'anywhere' }}>{e.text}</div>
                </div>
              )))}
            </div>
          )}
        </div>
      </div>

      {crmTarget && (
        <ClientCrmModal
          profileId={crmTarget.id}
          seed={crmTarget}
          initialAction={crmTarget.initialAction || null}
          isZh={isZh}
          onClose={() => setCrmTarget(null)}
          onChanged={() => setTick(n => n + 1)}
        />
      )}
    </div>
  );
}
